import AfSwitch from '@/components/AfSwitch';
import InputCheckboxWithTooltip from '@/components/InputCheckboxWithTooltip';
import InputNumberSpinner from '@/components/InputNumberSpinner';
import Upload, { UploadType } from '@/components/Upload';
import Constants from '@/Constants';
import { FileUploadData } from '@/hooks/upload/FileUpload';
import { EvaluationLesson } from '@/models/Lesson';
import { LessonData } from '@/models/LessonData';
import { courseService } from '@/services/CourseService';
import { ApplicationState } from '@/store';
import { setFloatingCourseActionsConfiguration } from '@/store/common/actions';
import { FloatingCourseActionsBarConfiguration } from '@/store/common/types';
import { getQuestionClear, getQuestionsRequest } from '@/store/evaluationQuestion/actions';
import { deleteLessonRequest, getLessonRequest, putEvaluationLessonRequest, setCurrentLessonForEdition } from '@/store/lesson/action';
import React, { SyntheticEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import QuestionList from '../QuestionList';
import { EvaluationLessonForm, evaluationlessonSchema, formInitialState } from './formConfig';
import QuestionModal from './QuestionModal';
import './style.scss';

interface Props {
    lesson: EvaluationLesson;
    onDelete: CallableFunction;
    onSavePressed: boolean;
    onPrevPressed: boolean;
    onNextPressed: boolean;
    preRequisitesEnabled: boolean;
    lessonLength: number;
}

const EvaluationCard: React.FC<Props> = (props: Props) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { identity, course, strings, currentLessonInEdition, success, questionInEdition, currentCourseInEdition, questionsSize } = useSelector(
        (state: ApplicationState) => ({
            strings: state.locale.data,
            identity: state.identity.data,
            course: state.lists.courseData,
            currentLessonInEdition: state.lessons.currentData as EvaluationLesson,
            success: state.lessons.success,
            questionInEdition: state.evaluationQuestion.data,
            currentCourseInEdition: state.lists.courseData,
            questionsSize: state.evaluationQuestion.list.questions?.length || 0,
        }),
    );
    const { lesson, onDelete, onSavePressed, onPrevPressed, onNextPressed, preRequisitesEnabled, lessonLength } = props;
    const [isEdit, setIsEdit] = useState(false);
    const [isEditingAllowed, setIsEditingAllowed] = useState(false);
    const [mainImagePreview, setMainImagePreview] = useState<string>();
    const [formIsValid, setFormIsValid] = useState<boolean>(false);
    const [formState, setFormState] = useState(formInitialState);
    const [showQuestionModal, setShowQuestionModal] = useState(false);
    const [saveAndClose, setSaveAndClose] = useState(false);
    const {
        name,
        description,
        mainImage,
        author,
        prerequisites,
        mandatory,
        saveHighestScore,
        lockAfterApproval,
        shuffleAlternatives,
        showResponseSummary,
        masteryScore,
        gradeWeight,
        maxTries,
        timeLimit,
        incGradeWeight,
        incTimeLimit,
        questionsAmount,
    }: EvaluationLessonForm = formState;

    const isNewLesson: boolean = lesson?.content?.author === '' && lesson?.content?.description === '' && lesson?.content?.name === '';

    useEffect(() => {
        if (isEdit) {
            onCancelClickHandler();
        }
    }, [strings._LANGUAGE]);

    useEffect(() => {
        if (currentLessonInEdition) {
            setIsEdit(currentLessonInEdition.id === lesson.id);
        }
    }, []);

    useEffect(() => {
        if (isEdit) {
            evaluationlessonSchema
                .validate(formState, { context: { totalQuestions: questionsSize } })
                .then(() => setFormIsValid(true))
                .catch(() => {
                    setFormIsValid(false);
                });
        }
    }, [isEdit, formState, questionsSize]);

    useEffect(() => {
        if (isEdit) {
            if (lesson.id) {
                dispatch(getLessonRequest(lesson.id, false, true));
            }
        }
    }, [dispatch, isEdit]);

    useEffect(() => {
        if (lesson.id) {
            //Exibição do card
            batchUpdateForm(lesson);
        } else {
            //Criação de nova lição
            setIsEdit(true);

            const lesonsElements: HTMLDivElement[] = (document.getElementsByClassName('lesson-card') as unknown) as HTMLDivElement[];
            const newLessonElement: HTMLDivElement = (lesonsElements[lesonsElements.length - 1] as unknown) as HTMLDivElement;

            window.scroll({
                behavior: 'smooth',
                left: 0,
                top: getOffsetTop(newLessonElement) - newLessonElement.clientHeight,
            });
        }
    }, [lesson]);

    useEffect(() => {
        setIsEditingAllowed(!currentLessonInEdition || currentLessonInEdition.id === lesson.id);
        if (currentLessonInEdition && currentLessonInEdition.id && currentLessonInEdition.id === lesson.id && currentLessonInEdition.name !== '') {
            //Carregando os campos para edição da lição
            batchUpdateForm(currentLessonInEdition);
        }
    }, [currentLessonInEdition, lesson]);

    useEffect(() => {
        if (currentLessonInEdition) {
            dispatch(
                setFloatingCourseActionsConfiguration({
                    saveDisabled: !formIsValid,
                    previewDisabled: !formIsValid,
                } as FloatingCourseActionsBarConfiguration),
            );
        }
    }, [formIsValid, currentLessonInEdition]);

    const batchUpdateForm = (editingLesson: EvaluationLesson) => {
        handleOnChange('mainImage', editingLesson.mainImage ? editingLesson.mainImage : formInitialState.mainImage);
        handleOnChange('name', editingLesson.name);
        handleOnChange('author', editingLesson.content.author);
        handleOnChange('description', editingLesson.description);
        handleOnChange('locale', editingLesson.content.locale);
        handleOnChange('mandatory', editingLesson.mandatory);
        handleOnChange('prerequisites', editingLesson.prerequisites);

        handleOnChange(
            'saveHighestScore',
            editingLesson.saveHighestScore != undefined ? editingLesson.saveHighestScore : formInitialState.saveHighestScore,
        );
        handleOnChange(
            'lockAfterApproval',
            editingLesson.lockAfterApproval != undefined ? editingLesson.lockAfterApproval : formInitialState.lockAfterApproval,
        );
        handleOnChange(
            'shuffleAlternatives',
            editingLesson.shuffleAlternatives != undefined ? editingLesson.shuffleAlternatives : formInitialState.shuffleAlternatives,
        );
        handleOnChange(
            'showResponseSummary',
            editingLesson.showResponseSummary != undefined ? editingLesson.showResponseSummary : formInitialState.showResponseSummary,
        );

        handleOnChange('masteryScore', editingLesson.masteryScore != undefined ? editingLesson.masteryScore : formInitialState.masteryScore);
        handleOnChange('gradeWeight', editingLesson.gradeWeight != undefined ? editingLesson.gradeWeight : formInitialState.gradeWeight);
        handleOnChange('maxTries', editingLesson.maxTries != undefined ? editingLesson.maxTries : formInitialState.maxTries);
        handleOnChange('timeLimit', editingLesson.timeLimit != undefined ? editingLesson.timeLimit : formInitialState.timeLimit);
        handleOnChange('workload', editingLesson.timeLimit != undefined ? editingLesson.timeLimit : formInitialState.timeLimit);

        if (editingLesson.gradeWeight != undefined && editingLesson.gradeWeight > 1) {
            handleOnChange('incGradeWeight', editingLesson.gradeWeight ? true : false);
        } else {
            handleOnChange('incGradeWeight', false);
        }

        if (editingLesson.timeLimit != undefined && editingLesson.timeLimit > 0) {
            handleOnChange('incTimeLimit', editingLesson.timeLimit ? true : false);
        } else {
            handleOnChange('incTimeLimit', false);
        }

        handleOnChange(
            'questionsAmount',
            editingLesson.questionsAmount != undefined ? editingLesson.questionsAmount : formInitialState.questionsAmount,
        );
    };

    const handleOnChange = (fieldName: string, value: string | number | boolean | object | null | undefined) => {
        setFormState((prevState) => ({ ...prevState, [fieldName]: value }));
    };

    const onEditClickHandler = (e: SyntheticEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsEdit(true);
        dispatch(setCurrentLessonForEdition(lesson));
    };

    useEffect(() => {
        if (lessonLength == 1) setFormState((formState) => ({ ...formState, prerequisites: false }));
    }, [lessonLength]);

    const saveLesson = async () => {
        const data = {
            evaluationLesson: {
                ...lesson,
                ...formState,
                gradeWeight: incGradeWeight ? gradeWeight : 1,
                maxTries: maxTries,
                timeLimit: incTimeLimit ? timeLimit : 0,
                workload: incTimeLimit ? timeLimit : 0,
                mediaType: null,
                course: { id: course.id },
                locale: course.locale,
                applicationId: Constants.APP_ID,
                organizationId: identity.user.orgId,
                showCoverImage: mainImage ? true : false,
                questionsAmount,
            },
        } as unknown;
        dispatch(putEvaluationLessonRequest(data as LessonData));
    };

    const onSaveClickHandler = (e: SyntheticEvent) => {
        e.preventDefault();
        e.stopPropagation();
        saveLesson();
    };

    useEffect(() => {
        if (isEdit && (onNextPressed || onPrevPressed || onSavePressed)) {
            saveLesson();
        } else {
            redirect();
        }
    }, [onNextPressed, onPrevPressed, onSavePressed]);

    useEffect(() => {
        if (success && isEdit) {
            setIsEdit(false);
            dispatch(setCurrentLessonForEdition(undefined));
            dispatch(
                setFloatingCourseActionsConfiguration({
                    saveDisabled: false,
                    previewDisabled: false,
                } as FloatingCourseActionsBarConfiguration),
            );
        }
    }, [success, isEdit]);

    const redirect = () => {
        if (onSavePressed) {
            history.push('/contents/lms/courses');
        }
        if (onPrevPressed) {
            history.push(`/contents/lms/courses/${course.id}`);
        }
    };

    const onDeleteClickHandler = (e: SyntheticEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (lesson.id) {
            dispatch(deleteLessonRequest(lesson.id));
            onDelete(lesson.id);
        } else onDelete();
    };

    const onCancelClickHandler = (e?: SyntheticEvent) => {
        e && e.preventDefault();
        e && e.stopPropagation();

        setIsEdit(false);
        dispatch(setCurrentLessonForEdition(undefined));

        dispatch(
            setFloatingCourseActionsConfiguration({
                saveDisabled: true,
                previewDisabled: false,
            } as FloatingCourseActionsBarConfiguration),
        );

        if (isNewLesson) {
            dispatch(deleteLessonRequest(lesson.id));
            onDelete(lesson.id);
        } else if (!lesson.id) {
            onDelete();
        }
    };

    const getOffsetTop = (elem: HTMLElement) => {
        let offsetLeft: number = 0;
        do {
            if (!isNaN(elem.offsetLeft)) {
                offsetLeft += elem.offsetTop;
            }
        } while ((elem = elem.offsetParent as HTMLElement));

        return offsetLeft;
    };

    useEffect(() => {
        if (currentLessonInEdition && currentLessonInEdition.id === lesson.id) {
            dispatch(getQuestionsRequest(currentLessonInEdition.id, 'LESSON'));
        }
    }, []);

    useEffect(() => {
        if (saveAndClose) setSaveAndClose(false);
    }, [questionInEdition]);

    const onAddQuestionClickHandler = () => {
        dispatch(getQuestionClear());
        setShowQuestionModal(true);
    };

    const isCoursePublished: boolean = currentCourseInEdition.status === Constants.courseStatus.PUBLISHED;

    const questionsShownInfo: string = strings.AMOUNT_REGISTERED_QUESTIONS.replace('%n', questionsAmount.toString()).replace(
        '%t',
        questionsSize.toString(),
    );

    return (
        <>
            {!isEdit && (
                <div className="main">
                    <div className="lesson-card">
                        <div className="lesson-card__body">
                            <div>
                                <span className="lesson-card__label">{strings.EVALUATION}</span>
                                <span className="lesson-card__icon">
                                    <i className="material-icons-outlined align-middle">{Constants.icons.LIST_ALT}</i>
                                </span>
                            </div>
                            <h3 className="lesson-card__title" title={name}>
                                {name || strings.NO_TITLE}
                            </h3>
                            <p className="lesson-card__text" title={description}>
                                {description || strings.NO_DESCRIPTION}
                            </p>
                        </div>
                        <div className="lesson-card__edit-container">
                            <div className="lesson-card__edit-container__thumb">
                                {mainImage && <img alt="" className="lesson-card__edit-container__thumb__img" src={mainImagePreview || mainImage} />}
                                {!mainImage && (
                                    <div className="lesson-card__edit-container__empty-thumb">
                                        <div>
                                            <i className="material-icons icon">{Constants.icons.SCHOOL}</i>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="edit__author-container">
                            <div className="author">
                                <div className="lesson-card__author">{author || strings.DEFAULT_AUTHOR}</div>
                                <div className="lesson-card__evaluationinfo">
                                    <div>
                                        {lesson.totalQuestions} {lesson.totalQuestions === 1 ? strings.QUESTION : strings.QUESTIONS}
                                    </div>
                                    <div>
                                        {lesson.maxTries} {lesson.maxTries === 1 ? strings.ATTEMPT : strings.ATTEMPTS}
                                    </div>
                                    <div>
                                        {strings.MASTERY_SCORE}: {lesson.masteryScore}
                                    </div>
                                    <div>
                                        <i className="material-icons icon edit-icon">{Constants.icons.ALARM}</i>
                                        <span>{lesson.timeLimit ? courseService.formatWorkload(lesson.timeLimit, strings) : '-'}</span>
                                    </div>
                                </div>
                            </div>

                            {isEditingAllowed && (
                                <div className="lesson-card__edit-container__edit ">
                                    <a href="#" onClick={onEditClickHandler}>
                                        <span className="edit-btn">{strings.EDIT}</span>
                                        <i className="material-icons icon edit-icon">{Constants.icons.EDIT}</i>
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {isEdit && (
                <div className="main">
                    <div className="lesson-card ">
                        <div className="lesson-card__body">
                            <div>
                                <span className="lesson-card__label">{strings.EVALUATION}</span>
                                <span className="lesson-card__icon">
                                    <i className="material-icons-outlined align-middle">{Constants.icons.LIST_ALT}</i>
                                </span>
                            </div>

                            <input
                                className="lesson-card__title edit edit__title "
                                id="titleInput"
                                onChange={(e) => handleOnChange('name', e.target.value)}
                                placeholder={strings.NO_TITLE}
                                required
                                type="text"
                                value={name || ''}
                            />

                            <input
                                className="lesson-card__text edit edit__description"
                                id="descriptionInput"
                                onChange={(e) => handleOnChange('description', e.target.value)}
                                placeholder={strings.NO_DESCRIPTION}
                                required
                                type="text"
                                value={description || ''}
                            />
                        </div>

                        <div className="lesson-card__edit-container">
                            <div className="lesson-card__edit-container__upload-thumb ">
                                {!(mainImagePreview || mainImage) && (
                                    <Upload
                                        onComplete={(response: FileUploadData) => {
                                            handleOnChange('mainImage', response.key);
                                            setMainImagePreview(response.preview);
                                        }}
                                        onRemove={() => handleOnChange('mainImage', undefined)}
                                        typeAccepted="IMAGE"
                                        uploadType={UploadType.LESSON_THUMB}
                                    />
                                )}
                                {(mainImagePreview || mainImage) && (
                                    <img alt="" className="lesson-card__edit-container__thumb__img" src={mainImagePreview || mainImage} />
                                )}
                            </div>
                        </div>

                        <div className="edit__author-container">
                            <div className="lesson-card__edit-container__author">
                                <input
                                    className="lesson-card__author edit edit__author"
                                    id="authorInput"
                                    onChange={(e) => handleOnChange('author', e.target.value)}
                                    placeholder={strings.DEFAULT_AUTHOR}
                                    required
                                    type="text"
                                    value={author || ''}
                                />
                            </div>

                            {lesson && lesson.id && (
                                <div className="lesson-card__edit-container__delete">
                                    {!course.published && lesson.name !== '' && (
                                        <a href="#" onClick={onDeleteClickHandler}>
                                            <span className="delete-btn">{strings.DELETE}</span>
                                        </a>
                                    )}
                                </div>
                            )}

                            <div className="lesson-card__edit-container__cancel">
                                <a href="#" onClick={onCancelClickHandler}>
                                    <span className="cancel-btn">{strings.CANCEL}</span>
                                </a>
                            </div>

                            <div className="lesson-card__edit-container__save">
                                <button className="btn save-btn" disabled={!formIsValid} onClick={onSaveClickHandler}>
                                    <span className="save-btn">{strings.SAVE}</span>
                                    <i className="material-icons-outlined icon save-icon">{Constants.icons.SAVE}</i>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="edition-card">
                        <div className="input-section">
                            <QuestionList onAddQuestionClick={onAddQuestionClickHandler} setShowQuestionModal={setShowQuestionModal} />
                        </div>

                        <QuestionModal setSaveAndClose={setSaveAndClose} setShow={setShowQuestionModal} show={showQuestionModal} />

                        <div className="input-section edition-card__checkbox-container">
                            <InputCheckboxWithTooltip
                                disabled={!preRequisitesEnabled}
                                initialValue={prerequisites && preRequisitesEnabled}
                                label={strings.HAS_PREREQUISITE}
                                onChange={(val: boolean) => {
                                    handleOnChange('prerequisites', val);
                                }}
                                toolTip={strings.TOOLTIP_HAS_PREREQUISITE}
                            />
                            <InputCheckboxWithTooltip
                                disabled={isCoursePublished}
                                initialValue={mandatory}
                                label={strings.ITS_MANDATORY}
                                onChange={(val: boolean) => {
                                    handleOnChange('mandatory', val);
                                }}
                                toolTip={strings.TOOLTIP_ITS_MANDATORY}
                            />

                            <InputCheckboxWithTooltip
                                disabled={isCoursePublished}
                                initialValue={saveHighestScore}
                                label={strings.SAVE_HIGHEST_SCORE}
                                onChange={(val: boolean) => handleOnChange('saveHighestScore', val)}
                            />
                            <InputCheckboxWithTooltip
                                initialValue={lockAfterApproval}
                                label={strings.EVALUATION_BLOCKED_AFTER_APPROVAL}
                                onChange={(val: boolean) => handleOnChange('lockAfterApproval', val)}
                                toolTip={strings.TOOLTIP_EVALUATION_BLOCKED_AFTER_APPROVAL}
                            />
                            <InputCheckboxWithTooltip
                                disabled={isCoursePublished}
                                initialValue={showResponseSummary}
                                label={strings.SHOW_ANSWERS_AT_THE_END}
                                onChange={(val: boolean) => handleOnChange('showResponseSummary', val)}
                                toolTip={strings.TOOLTIP_SHOW_ANSWERS_AT_THE_END}
                            />
                        </div>

                        <InputNumberSpinner
                            info={questionsShownInfo}
                            initial={isNewLesson ? questionsSize : questionsAmount}
                            label={strings.QUESTIONS_SHOWN_IN_THE_ASSESSMENT}
                            max={questionsSize}
                            min={0}
                            onChange={(value: number) => handleOnChange('questionsAmount', value)}
                            readonly={isCoursePublished}
                            style={{ marginBottom: 16, marginTop: 0 }}
                            tooltip={strings.DEFINE_NUMBER_OF_QUESTIONS}
                        />

                        <InputNumberSpinner
                            initial={masteryScore}
                            label={strings.MINIMUM_SCORE_FOR_APPROVAL}
                            max={100}
                            min={0}
                            onChange={(val: number) => handleOnChange('masteryScore', val)}
                            readonly={isCoursePublished}
                            style={{ marginBottom: 16, marginTop: 0 }}
                            tooltip={strings.TOOLTIP_EVALUATION_MASTERYSCORE}
                        />
                        <InputNumberSpinner
                            initial={maxTries}
                            label={strings.NUMBER_OF_ATTEMPTS_ALLOWED}
                            max={99}
                            min={1}
                            onChange={(val: number) => handleOnChange('maxTries', val)}
                            readonly={isCoursePublished}
                            style={{ marginBottom: 32, marginTop: 0 }}
                            tooltip={strings.TOOLTIP_NUMBER_OF_ATTEMPTS_ALLOWED}
                            whenDisabledSetValueTo={1}
                        />
                        <hr />
                        <div className="input-section">
                            <h1 className="org-color-primary">{strings.OPTIONAL_INFO}</h1>
                            <div className="d-flex" style={{ marginTop: 24, paddingBottom: 16 }}>
                                <InputNumberSpinner
                                    disabled={!incGradeWeight}
                                    initial={gradeWeight}
                                    label={strings.EVALUATION_WEIGHT}
                                    max={1000}
                                    onChange={(val: number) => handleOnChange('gradeWeight', val)}
                                    readonly={isCoursePublished}
                                    tooltip={strings.TOOLTIP_EVALUATION_WEIGHT}
                                    whenDisabledSetValueTo={1}
                                />
                                <div className="input-section">
                                    <div className="field-switch">
                                        <AfSwitch
                                            checked={incGradeWeight}
                                            disabled={isCoursePublished}
                                            onChange={() => handleOnChange('incGradeWeight', !incGradeWeight)}
                                        />
                                        <span className={`${isCoursePublished ? 'disabled' : ''}`}>{strings.INC_EVALUATION_WEIGHT}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="d-flex" style={{ paddingBottom: 16 }}>
                                <InputNumberSpinner
                                    buttonBar={true}
                                    disabled={!incTimeLimit}
                                    hideValueIfDisabled={true}
                                    initial={timeLimit}
                                    label={strings.TIME_LIMIT}
                                    max={10080}
                                    onChange={(val: number) => handleOnChange('timeLimit', val)}
                                    readonly={isCoursePublished}
                                    tooltip={strings.TOOLTIP_TIME_LIMIT}
                                    whenDisabledSetValueTo={0}
                                />
                                <div className="input-section">
                                    <div className="field-switch">
                                        <AfSwitch
                                            checked={incTimeLimit}
                                            disabled={isCoursePublished}
                                            onChange={() => {
                                                handleOnChange('timeLimit', incTimeLimit ? 0 : 60);
                                                handleOnChange('incTimeLimit', !incTimeLimit);
                                            }}
                                        />
                                        <span className={`${isCoursePublished ? 'disabled' : ''}`}>{strings.INC_TIME_LIMIT}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default EvaluationCard;
